import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../lib/auth';

/**
 * Reset-password screen. Reached from the emailed link, which looks like:
 *   /reset-password?token=<one-time-token>
 *
 * The token isn't a JWT — it's a short-lived single-use string the backend
 * minted when the user requested a reset, and only the backend can verify
 * it. We POST {token, newPassword} to /api/auth/reset-password; on success
 * we redirect to /login?reset=1 (the login page shows a success banner).
 *
 * Missing token → render a "link invalid" state. We don't try to be clever
 * about partially-bad tokens; any 4xx from the API collapses to the same
 * generic "expired or invalid" message — the user can request a fresh link.
 */

const MIN_PASSWORD_LEN = 10;

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <Centered>
        <h1 className="text-[22px] font-bold tracking-tight">Reset link incomplete</h1>
        <p className="mt-2 text-[14px] text-muted leading-relaxed">
          This URL is missing its reset token. Request a fresh link to try again.
        </p>
        <div className="mt-5 flex items-center gap-3 text-[12.5px]">
          <Link to="/forgot-password" className="btn-primary">
            Request new link
          </Link>
          <Link to="/login" className="btn-ghost">
            Back to sign in
          </Link>
        </div>
      </Centered>
    );
  }

  const passwordOk = password.length >= MIN_PASSWORD_LEN;
  const confirmOk = confirm.length > 0 && confirm === password;
  const canSubmit = !pending && passwordOk && confirmOk;

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setPending(true);
    try {
      await resetPassword(token, password);
      navigate('/login?reset=1', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error && /weak/i.test(err.message)
          ? `Pick a password with at least ${MIN_PASSWORD_LEN} characters.`
          : 'This reset link has expired or is invalid. Request a new one.',
      );
      setPending(false);
    }
  }

  // If somehow they got here with a session, kick them home — the reset flow
  // assumes a signed-out user. Could happen if they open the email link in a
  // tab that still has a stale session.
  if (typeof window !== 'undefined' && localStorage.getItem('thren.session.v2')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Centered>
      <h1 className="text-[22px] font-bold tracking-tight">Set a new password</h1>
      <p className="mt-2 text-[14px] text-muted leading-relaxed">
        Pick something at least {MIN_PASSWORD_LEN} characters long. After this
        you&rsquo;ll be sent back to the sign-in screen.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-3.5" noValidate>
        <label htmlFor="password" className="block">
          <span className="block text-[11.5px] uppercase tracking-wider text-muted font-semibold mb-1.5">
            New password
          </span>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            disabled={pending}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md bg-paper2 border border-rule px-3 py-2.5 text-[14px] text-ink placeholder:text-muted/45 outline-none transition-colors focus:border-electric-500/60 focus:bg-paper2 disabled:opacity-60"
          />
          <p
            className={
              passwordOk
                ? 'mt-1.5 text-[11.5px] text-candy-lime'
                : 'mt-1.5 text-[11.5px] text-muted'
            }
          >
            {passwordOk
              ? 'Length looks good.'
              : `${password.length} / ${MIN_PASSWORD_LEN} characters`}
          </p>
        </label>

        <label htmlFor="confirm" className="block">
          <span className="block text-[11.5px] uppercase tracking-wider text-muted font-semibold mb-1.5">
            Confirm new password
          </span>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            disabled={pending}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-md bg-paper2 border border-rule px-3 py-2.5 text-[14px] text-ink placeholder:text-muted/45 outline-none transition-colors focus:border-electric-500/60 focus:bg-paper2 disabled:opacity-60"
          />
          {confirm.length > 0 && !confirmOk && (
            <p className="mt-1.5 text-[11.5px] text-candy-pink">
              Passwords don&rsquo;t match yet
            </p>
          )}
        </label>

        {error && (
          <p role="alert" className="text-[12.5px] text-candy-pink leading-relaxed">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? 'Updating…' : 'Update password'}
        </button>

        <p className="text-center text-[12.5px] text-muted pt-1">
          <Link to="/login" className="text-accent hover:underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </Centered>
  );
}

/** Shared visual chrome for both the form state and the empty-token state. */
function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen relative grid place-items-center px-5 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0  opacity-80" />
      <div aria-hidden="true" className="absolute inset-x-0 top-1/4 h-[400px] " />

      <div className="relative w-full max-w-[420px]">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="w-8 h-8 rounded-md bg-ink text-paper grid place-items-center font-extrabold text-[15px] leading-none" aria-hidden="true">T</span>
          <span className="font-bold tracking-tight text-[20px] text-ink">Thren</span>
        </div>
        <div className="surface p-7">{children}</div>
      </div>
    </div>
  );
}
